import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Snackbar,
    Switch,
    Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { api } from "common";
import Button from "components/CustomButtons/Button.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AlertDialog from "./AlertDialog";
import CircularLoading from "./CircularLoading";
import RoundOffConfig from "./RoundOffConfig";
import { colors } from "./Theme/WebTheme";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    card: {
        marginBottom: theme.spacing(3),
        borderRadius: 12,
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        border: "1px solid #e0e0e0",
    },
    textField: {
        marginTop: theme.spacing(2),
        width: "100%",
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    title: {
        fontSize: "1.25rem",
        fontWeight: "bold",
        color: "#333",
        marginBottom: theme.spacing(1),
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    description: {
        fontSize: "0.875rem",
        color: "#666",
        marginTop: theme.spacing(1),
        lineHeight: 1.5,
    },
    switchContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
    },
    switch: {
        marginLeft: theme.spacing(2),
    },
    buttonStyle: {
        margin: theme.spacing(2, 0),
        width: "100%",
        height: 40,
        borderRadius: "30px",
        backgroundColor: colors.MAIN_COLOR,
        color: colors.WHITE,
    },
    loadingContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing(4),
    },
}));

export default function AddOnSettings() {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const { editSettings, clearSettingsViewError, updateAddOn } = api;
    const settingsdata = useSelector((state) => state.settingsdata);
    const addonsdata = useSelector((state) => state.addondata);
    const auth = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    // Simple debugging
    console.log("🔍 AddOnSettings Component State:", {
        componentMounted: true,
        settingsdata: !!settingsdata,
        hasSettings: !!settingsdata?.settings,
        addonsdata: !!addonsdata,
        hasAddonsData: !!addonsdata?.data,
        addonsDataContent: addonsdata?.data,
        auth: !!auth,
        api: !!editSettings,
    });

    // Initialize data state with default values
    const [data, setData] = useState({
        sub_admin_controls: false,
        document_management: false,
        multi_service: false,
        enable_subscription: false,
        servicePricingDisplay: false,
        trip_amount_roundoff: {
            enabled: false,
            adjustToAmount: 100,
            roundBy: "last_two_digits",
            thresholdAmount: 50,
        },
    });
    const [clicked, setClicked] = useState(false);
    const [localData, setLocalData] = useState({});
    const [notification, setNotification] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    // Memoize initial data to prevent unnecessary re-renders
    const initialData = useMemo(() => {
        const defaultRoundoff = {
            enabled: false,
            adjustToAmount: 100,
            roundBy: "last_two_digits",
            thresholdAmount: 50,
        };

        const addonData = addonsdata.data || {};

        // Merge roundoff settings properly to ensure enabled field exists
        const mergedRoundoff = addonData.trip_amount_roundoff
            ? { ...defaultRoundoff, ...addonData.trip_amount_roundoff }
            : defaultRoundoff;

        return {
            sub_admin_controls: false,
            document_management: false,
            multi_service: false,
            enable_subscription: false,
            servicePricingDisplay: false,
            // Get addon fields from addons, others from settings
            ...settingsdata.settings,
            ...addonData,
            // Override roundoff with properly merged version to ensure enabled field exists
            trip_amount_roundoff: mergedRoundoff,
        };
    }, [settingsdata.settings, addonsdata.data]);

    useEffect(() => {
        console.log("🔄 useEffect triggered:", !!settingsdata?.settings);

        if (settingsdata.settings) {
            setData(initialData);
            setLocalData({});

            console.log("🔄 Setting data from initialData:", initialData);
        }
    }, [initialData, settingsdata.settings]);

    // Memoized handlers to prevent re-renders
    const handleSwitchChange = useCallback((e) => {
        const fieldName = e.target.name;
        const newValue = e.target.checked;

        console.log("🔄 Switch changed:", fieldName, "=", newValue);

        // Update local data only - no re-render
        setLocalData((prev) => ({ ...prev, [fieldName]: newValue }));
    }, []);

    const handleTextChange = useCallback((e) => {
        const fieldName = e.target.name;
        const newValue = e.target.value;

        console.log("🔄 Text changed:", fieldName, "=", newValue);
    }, []);

    const handleSubmit = useCallback(
        (e) => {
            e.preventDefault();
            if (data.AllowCriticalEditsAdmin) {
                setClicked(true);

                // Merge local changes with original data
                const finalData = { ...data, ...localData };

                console.log("💾 Saving addon and settings data:", finalData);

                // Separate addon data from settings data
                const addonFields = [
                    "multi_service",
                    "document_management",
                    "sub_admin_controls",
                    "enable_subscription",
                    "servicePricingDisplay",
                    "trip_amount_roundoff",
                ];

                const addonData = {};
                const settingsData = { ...finalData };

                // Extract addon fields
                addonFields.forEach((field) => {
                    if (finalData.hasOwnProperty(field)) {
                        addonData[field] = finalData[field];
                        // Only remove roundoff from settings, other addons can be in both places
                        if (field === "trip_amount_roundoff") {
                            delete settingsData[field];
                        }
                    }
                });

                console.log("💾 Addon data to save:", addonData);
                console.log("💾 Settings data to save:", settingsData);
                console.log("💾 Current addonsdata.data:", addonsdata.data);
                console.log("💾 Merged addon data:", {
                    ...addonsdata.data,
                    ...addonData,
                });

                // Save addon data first, then settings
                const savePromises = [];

                if (Object.keys(addonData).length > 0) {
                    const addonPromise = new Promise((resolve, reject) => {
                        dispatch(
                            updateAddOn(
                                { ...addonsdata.data, ...addonData },
                                (error) =>
                                    reject(
                                        new Error(`Addon save failed: ${error}`)
                                    ),
                                () => resolve("Addon saved")
                            )
                        );
                    });
                    savePromises.push(addonPromise);
                }

                if (Object.keys(settingsData).length > 0) {
                    savePromises.push(dispatch(editSettings(settingsData)));
                }

                Promise.all(savePromises)
                    .then(() => {
                        console.log("✅ All data saved successfully");
                        setClicked(false);
                        setLocalData({}); // Clear local changes
                        setData(finalData); // Update main data

                        // Show success notification
                        setNotification({
                            open: true,
                            message: "Settings saved successfully!",
                            severity: "success",
                        });
                    })
                    .catch((error) => {
                        console.error("❌ Error saving data:", error);
                        setClicked(false);

                        // Show error notification
                        setNotification({
                            open: true,
                            message: `Error saving settings: ${error.message}`,
                            severity: "error",
                        });
                    });
            } else {
                console.log("❌ No permission to edit settings");
                alert(t("demo_mode"));
            }
        },
        [
            data,
            localData,
            dispatch,
            addonsdata.data,
            updateAddOn,
            editSettings,
            t,
        ]
    );

    const handleClose = useCallback(() => {
        setClicked(false);
        dispatch(clearSettingsViewError());
    }, [dispatch, clearSettingsViewError]);

    // Memoized display data to prevent re-renders
    const displayData = useMemo(() => {
        return { ...data, ...localData };
    }, [data, localData]);

    if (!settingsdata.settings || !addonsdata.data) {
        console.log(
            "⏳ No settings or addons data available, showing loading..."
        );
        return (
            <div className={classes.loadingContainer}>
                <CircularLoading />
            </div>
        );
    }

    // Simple render debugging
    console.log("🎨 Rendering AddOnSettings:", {
        hasData: !!data,
        dataKeys: Object.keys(data || {}),
        manualWithdrawValue: data?.manual_withdraw_add_on,
        shareTripValue: data?.share_trip,
    });

    return (
        <div className={classes.root}>
            <Typography
                variant="h4"
                component="h1"
                gutterBottom
                style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
            >
                {t("add_on_settings")}
            </Typography>

            <Typography
                variant="body1"
                color="textSecondary"
                gutterBottom
                style={{ textAlign: isRTL === "rtl" ? "right" : "left" }}
            >
                {t("add_on_settings_description")}
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    {/* Left Column */}
                    <Grid item xs={12} md={6}>
                        {/* Sub Admin Controls Card */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <div className={classes.switchContainer}>
                                    <div>
                                        <Typography className={classes.title}>
                                            {t("sub_admin_controls")}
                                        </Typography>
                                        <Typography
                                            className={classes.description}
                                        >
                                            {t(
                                                "sub_admin_controls_description"
                                            )}
                                        </Typography>
                                    </div>
                                    <Switch
                                        className={classes.switch}
                                        checked={displayData.sub_admin_controls}
                                        onChange={handleSwitchChange}
                                        name="sub_admin_controls"
                                        color="primary"
                                        disabled={false}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column */}
                    <Grid item xs={12} md={6}>
                        {/* Document Management Card */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <div className={classes.switchContainer}>
                                    <div>
                                        <Typography className={classes.title}>
                                            {t("document_management") ||
                                                "Document Management"}
                                        </Typography>
                                        <Typography
                                            className={classes.description}
                                        >
                                            {t(
                                                "document_management_description"
                                            ) ||
                                                "Enable document management system for drivers and users to upload and manage required documents."}
                                        </Typography>
                                    </div>
                                    <Switch
                                        className={classes.switch}
                                        checked={
                                            displayData.document_management
                                        }
                                        onChange={handleSwitchChange}
                                        name="document_management"
                                        color="primary"
                                        disabled={false}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Multi-Service Card - Full Width */}
                    <Grid item xs={12}>
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <div className={classes.switchContainer}>
                                    <div>
                                        <Typography className={classes.title}>
                                            {t("multi_service") ||
                                                "Multi-Service"}
                                        </Typography>
                                        <Typography
                                            className={classes.description}
                                        >
                                            {t("multi_service_description") ||
                                                "Enable multi-service functionality allowing drivers to offer multiple categories and service types simultaneously. When enabled, drivers can select multiple car categories and types when adding vehicles."}
                                        </Typography>
                                    </div>
                                    <Switch
                                        className={classes.switch}
                                        checked={displayData.multi_service}
                                        onChange={handleSwitchChange}
                                        name="multi_service"
                                        color="primary"
                                        disabled={false}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Enable Subscription Card - Full Width */}
                    {/* <Grid item xs={12}>
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <div className={classes.switchContainer}>
                                    <div>
                                        <Typography className={classes.title}>
                                            {t("enable_subscription_menu")}
                                        </Typography>
                                        <Typography
                                            className={classes.description}
                                        >
                                            {t(
                                                "enable_subscription_menu_description"
                                            )}
                                        </Typography>
                                    </div>
                                    <Switch
                                        className={classes.switch}
                                        checked={
                                            displayData.enable_subscription
                                        }
                                        onChange={handleSwitchChange}
                                        name="enable_subscription"
                                        color="primary"
                                        disabled={false}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </Grid> */}

                    {/* Service Pricing Display Card - Full Width */}
                    <Grid item xs={12}>
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <div className={classes.switchContainer}>
                                    <div>
                                        <Typography className={classes.title}>
                                            {t("service_pricing_display") ||
                                                "Service Pricing Display"}
                                        </Typography>
                                        <Typography
                                            className={classes.description}
                                        >
                                            {t(
                                                "service_pricing_display_description"
                                            ) ||
                                                "Enable enhanced pricing display on mobile app service selection screen. Shows detailed pricing information including base rate, minimum fare, and waiting charges for better transparency."}
                                        </Typography>
                                    </div>
                                    <Switch
                                        className={classes.switch}
                                        checked={
                                            displayData.servicePricingDisplay
                                        }
                                        onChange={handleSwitchChange}
                                        name="servicePricingDisplay"
                                        color="primary"
                                        disabled={false}
                                    />
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>

                    <RoundOffConfig
                        data={displayData}
                        setLocalData={setLocalData}
                        handleTextChange={handleTextChange}
                        handleSwitchChange={handleSwitchChange}
                    />

                    {/* Save Button Section - Full Width */}
                    <Grid item xs={12}>
                        <Box>
                            <Typography
                                variant="h6"
                                gutterBottom
                                style={{
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("settings_info")}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                                paragraph
                                style={{
                                    textAlign:
                                        isRTL === "rtl" ? "right" : "left",
                                }}
                            >
                                {t("add_on_settings_info")}
                            </Typography>

                            <Button
                                size="lg"
                                onClick={handleSubmit}
                                variant="contained"
                                color="success"
                                className={classes.buttonStyle}
                                disabled={
                                    !data.AllowCriticalEditsAdmin ||
                                    Object.keys(localData).length === 0
                                }
                            >
                                {clicked ? (
                                    <>
                                        <CircularProgress
                                            size={20}
                                            style={{
                                                marginRight: 8,
                                                color: "white",
                                            }}
                                        />
                                        {t("saving")}
                                    </>
                                ) : (
                                    <>{t("save_settings")}</>
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>

            <AlertDialog
                open={settingsdata.error.flag && clicked}
                onClose={handleClose}
            >
                {t("update_failed")}
            </AlertDialog>

            {/* Success Notification */}
            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() =>
                    setNotification({ ...notification, open: false })
                }
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() =>
                        setNotification({ ...notification, open: false })
                    }
                    severity={notification.severity}
                    sx={{ width: "100%" }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </div>
    );
}
